import { CardString, Hand, Suit, Value } from 'pokersolver'
import { BotActionCallbackArgs, getPlayingCards } from './StreetActions'
interface StraightDrawResult {
  outs: number
  lowCard: number
  highCard?: number
  missingCard?: number
}

function hasOpenEndedStraightDraw(ranks: number[]): StraightDrawResult | null {
  const uniqueRanks: number[] = [...new Set(ranks)].sort((a, b) => a - b)
  for (let i = 0; i < uniqueRanks.length - 3; i++) {
    const window: number[] = uniqueRanks.slice(i, i + 4)
    const min: number = Math.min(...window)
    const max: number = Math.max(...window)
    if (max - min <= 4 && window.length === 4) {
      const lowCard: number = min - 1
      const highCard: number = max + 1
      const outs: number = [lowCard, highCard].filter((card: number) => card >= 2 && card <= 14).length * 4
      return { outs, lowCard, highCard }
    }
  }
  return null
}

function hasGutshotStraightDraw(ranks: number[]): StraightDrawResult | null {
  const uniqueRanks: number[] = [...new Set(ranks)].sort((a, b) => a - b)
  for (let i = 0; i < uniqueRanks.length - 3; i++) {
    const window: number[] = uniqueRanks.slice(i, i + 4)
    const min: number = Math.min(...window)
    const max: number = Math.max(...window)
    if (max - min === 4 && window.length === 4) {
      for (let k = min; k <= max; k++) {
        if (!window.includes(k)) {
          const missingCard: number = k
          return { outs: 4, missingCard, lowCard: missingCard }
        }
      }
    }
  }
  return null
}

// Define interfaces for draw analysis
interface DrawOdds {
  outs: number
  turnOdds: number
  riverOdds: number
  completes?: boolean
}

interface DrawAnalysis {
  currentHand: string
  isStrong: boolean
  draws: string[]
  odds: {
    flushDraw?: DrawOdds
    openEndedStraightDraw?: DrawOdds
    gutshotStraightDraw?: DrawOdds
  }
}

// Function to analyze draws using PokerSolver
export function analyzeDraws({ table, player }: Omit<BotActionCallbackArgs, 'game'>): DrawAnalysis {
  const cards = getPlayingCards({ player, table })

  const solvedHand = Hand.solve(cards)

  const result: DrawAnalysis = {
    currentHand: solvedHand.descr, // e.g., "Ace High", "Pair of Kings"
    isStrong: false,
    draws: [],
    odds: {}
  }

  if (
    solvedHand.descr.includes('Flush') ||
    solvedHand.descr.includes('Straight') ||
    solvedHand.descr.includes('Full House') ||
    solvedHand.descr.includes('Three of a Kind') ||
    solvedHand.descr.includes('Four of a Kind') ||
    solvedHand.descr.includes('Straight Flush') ||
    solvedHand.descr.includes('Royal Flush')
  ) {
    // Skip draw detection if a strong hand is already made
    result.isStrong = true
    return result
  }

  // Get ranks and suits from cardPool (all cards) for draw detection
  const cardPool = solvedHand.cardPool.map((card) => ({
    rank: card.rank as number, // Numeric rank (2-14, Ace=14)
    value: card.value as Value, // Numeric rank (2-14, Ace=14)
    suit: card.suit as Suit // 'h', 's', 'd', 'c'
  }))

  // 1. Check for Flush Draw (4 cards of the same suit)
  const suitCounts = cardPool.reduce(
    (acc, card) => {
      acc[card.suit] = (acc[card.suit] || 0) + 1
      return acc
    },
    {} as Record<string, number>
  )

  const flushSuit = Object.keys(suitCounts).find((suit) => suitCounts[suit] === 4)
  if (flushSuit) {
    const outs = 13 - suitCounts[flushSuit] // 13 cards per suit - seen cards
    const turnOdds = outs * 2 // Approx. % to hit on turn
    const riverOdds = outs * 4 // Approx. % to hit by river
    result.draws.push('Flush Draw')
    result.odds.flushDraw = { outs, turnOdds, riverOdds }
  }

  // 2. Check for Open-Ended Straight Draw
  const ranks = cardPool.map((card) => card.rank)
  const openEnded: StraightDrawResult | null = hasOpenEndedStraightDraw(ranks)
  if (openEnded) {
    const { outs } = openEnded
    const turnOdds = outs * 2
    const riverOdds = outs * 4
    result.draws.push('Open-Ended Straight Draw')
    result.odds.openEndedStraightDraw = { outs, turnOdds, riverOdds }
  }

  // 3. Check for Gutshot Straight Draw
  const gutshot: StraightDrawResult | null = hasGutshotStraightDraw(ranks)
  if (gutshot) {
    const { outs } = gutshot
    const turnOdds: number = outs * 2
    const riverOdds: number = outs * 4
    result.draws.push('Gutshot Straight Draw')
    result.odds.gutshotStraightDraw = { outs, turnOdds, riverOdds }
  }

  // 4. Simulate Draw Completion
  function simulateDrawCompletion(drawType: string, testCard: { rank: number; suit: string }): boolean {
    const rankMap: { [key: number]: string } = {
      13: 'A',
      12: 'K',
      11: 'Q',
      10: 'J',
      9: 'T',
      8: '9',
      7: '8',
      6: '7',
      5: '6',
      4: '5',
      3: '4',
      2: '3',
      1: '2'
    }

    const cardStr: string = (rankMap[testCard.rank] || testCard.rank.toString()) + testCard.suit
    const newBoard: string[] = [...table.communityCards, cardStr]
    const newHand: Hand = Hand.solve([...player.cards, ...newBoard] as CardString[])

    if (drawType === 'Flush Draw' && newHand.descr.includes('Flush')) {
      return true
    }

    if (
      (drawType === 'Open-Ended Straight Draw' || drawType === 'Gutshot Straight Draw') &&
      newHand.descr.includes('Straight')
    ) {
      return true
    }
    return false
  }

  // Simulate for flush draw if present
  if (flushSuit) {
    // Dynamically select a test card rank not in cardPool
    const usedRanks: number[] = cardPool.filter((card) => card.suit === flushSuit).map((card) => card.rank)
    const possibleRanks: number[] = Array.from({ length: 13 }, (_, i) => i + 1).filter(
      (rank: number) => !usedRanks.includes(rank)
    )
    const randomIndex = Math.floor(Math.random() * possibleRanks.length)
    const testRank: number = possibleRanks[randomIndex] || 2 // Fallback to 2 if no ranks available
    const testCard: { rank: number; suit: string } = { rank: testRank, suit: flushSuit }
    // biome-ignore lint/style/noNonNullAssertion: <explanation>
    result.odds.flushDraw!.completes = simulateDrawCompletion('Flush Draw', testCard)
  }

  if (openEnded) {
    // Simulate for straight draws if present
    const testCard: { rank: number; suit: string } = { rank: openEnded.lowCard, suit: 'h' }
    // biome-ignore lint/style/noNonNullAssertion: <explanation>
    result.odds.openEndedStraightDraw!.completes = simulateDrawCompletion('Open-Ended Straight Draw', testCard)
  }
  if (gutshot) {
    // biome-ignore lint/style/noNonNullAssertion: <explanation>
    const testCard: { rank: number; suit: string } = { rank: gutshot.missingCard!, suit: 'h' }
    // biome-ignore lint/style/noNonNullAssertion: <explanation>
    result.odds.gutshotStraightDraw!.completes = simulateDrawCompletion('Gutshot Straight Draw', testCard)
  }

  return result
}
